import React, { useEffect, useRef, useState } from "react";
import "../../styles/focusSoundMusic.css";
import arrow_back from "../../assets/icons/arrow_back.svg";
import logo from "../../assets/icons/zensory_logo.svg";
import cross from "../../assets/icons/close.svg";
import pause from "../../assets/icons/pause.svg";
import play from "../../assets/icons/play.svg";
import Carousel from "../Carousel";
import { withScorm } from "react-scorm-provider";
import { ZensoryModule } from "../../ZensoryModule";
import { AnalyticsScreenName } from "../API/AnalyticsApi";
import { RouteId } from "../../Routes";
import { getText, TextId } from "../Util/TextHelper";
import drift from "../../assets/audio/Music/focus-sound/20s/DRIFT_85BMP_20sec.mp3"
import expand from "../../assets/audio/Music/focus-sound/20s/EXPAND_70BPM_20sec.mp3"
import flow from "../../assets/audio/Music/focus-sound/20s/FLOW_80BPM_20sec.mp3"
import ignite from "../../assets/audio/Music/focus-sound/20s/IGNITE_100BPM_20sec.mp3"
import lift from "../../assets/audio/Music/focus-sound/20s/LIFT_90BPM_20sec.mp3"
import open from "../../assets/audio/Music/focus-sound/20s/OPEN_95BPM_20sec.mp3"
import settle from "../../assets/audio/Music/focus-sound/20s/SETTLE_75BPM_20sec.mp3"
import {
  isSubmitKey,
  preventDefault,
  stopPropagation,
} from "../UI/KeyPressHelper";
import CloseAndExitPopup from "../Close&ExitPopup";
import { GlobalAppState } from "../AppState";
import { LOGGER, LogLevel } from "../Util/Logger";

const FocusSoundMusicTrackPage = (props) => {
  ZensoryModule.getAnalytics().trackScreen(AnalyticsScreenName.ScreenCustomise);
  const isShowExitButton = ZensoryModule.getFeatureUi().isShowExitButton();
  const isShowBackButton = ZensoryModule.getFeatureUi().isShowBackButton();
  const [showCloseAndExitPopup, setShowCloseAndExitPopup] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const getAudioUrlForExperience = () => {
    switch (props.experience) {
      case "drift":
        return drift;
      case "expand":
        return expand;
      case "flow":
        return flow;
      case "ignite":
        return ignite;
      case "lift":
        return lift;
      case "open":
        return open;
      case "settle":
        return settle;
      default:
        return flow;
    }
  };

  useEffect(() => {
    const cleanupAudio = () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        setCurrentTime(0);
        setIsPlaying(false);
        audio.removeEventListener("ended", handleAudioEnded);
        audio.removeEventListener("loadedmetadata", handleAudioData);
        audio.removeEventListener("timeupdate", handleAudioTime);
        audioRef.current = null;
      }
    };

    if (!props.experience) {
      cleanupAudio();
      return;
    }

    const audioUrl = getAudioUrlForExperience();
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handleAudioEnded = () => {
      setTimeout(() => {
        setIsPlaying(false);
        audio.currentTime = 0;
      }, 1000);
    };
    const handleAudioData = () => setDuration(audio.duration);
    const handleAudioTime = () => setCurrentTime(audio.currentTime);

    audio.addEventListener("ended", handleAudioEnded);
    audio.addEventListener("loadedmetadata", handleAudioData);
    audio.addEventListener("timeupdate", handleAudioTime);

    return cleanupAudio;
  }, [props.experience]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio
          .play()
          .catch((e) => LOGGER.log(LogLevel.ERROR, `Audio play error: ${e}`));
      } else {
        audio.pause();
      }
    }
  }, [isPlaying]);

  const handleNextClick = (e) => {
    preventDefault(e);
    ZensoryModule.getNavigationHandler().navigate(
      window,
      RouteId.FocusSoundNatureTrack,
      props.onexperienceSummary,
      props.onChooseNaturescape
    );
  };

  const togglePlayPause = () => {
    setIsPlaying((prev) => !prev);
  };

  const back = (e) => {
    preventDefault(e);
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      }
    }
    ZensoryModule.getNavigationHandler().back(
      window,
      props.onClose,
      props.onChooseNaturescape,
      false,
      GlobalAppState.getIsDeepLink()
    );
  };

  const close = (e) => {
    preventDefault(e);
    setShowCloseAndExitPopup(true);
    ZensoryModule.getNavigationHandler().exit(window, props.sco);
  };

  const _handleKeyDownBack = (e) => {
    stopPropagation(e);
    if (isSubmitKey(e)) {
      back(e);
    }
  };

  const _handleKeyDownCloseExit = (e) => {
    stopPropagation(e);
    if (isSubmitKey(e)) {
      close(e);
    }
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {showCloseAndExitPopup ? (
        <CloseAndExitPopup
          setShowCloseAndExitPopup={setShowCloseAndExitPopup}
        />
      ) : null}
      <div className="header-row-choose">
        {isShowBackButton ? (
          <div
            className="back"
            id="btn-choose-naturescape-back"
            onClick={(e) => back(e)}
            tabIndex="0"
            onKeyDown={(e) => _handleKeyDownBack(e)}
            role="button"
            alt={getText(TextId.backButtonAlt, {
              GENERIC: "to Choose a Zense",
            })}
            aria-label={getText(TextId.backButtonAlt, {
              GENERIC: "to Choose a Zense",
            })}
          >
            <img
              src={arrow_back}
              style={{ marginTop: "2px" }}
              alt="Back to Choose a Zense"
            />
            <span>{GlobalAppState.getIsDeepLink() ? "Home" : "Zenses"}</span>
          </div>
        ) : (
          <div className="placeholder"></div>
        )}
        {isShowExitButton ? (
          <div
            className="close"
            id="btn-choose-naturescape-close"
            onClick={(e) => close(e)}
            tabIndex="0"
            onKeyDown={(e) => _handleKeyDownCloseExit(e)}
            alt={getText(TextId.mainCloseAndExitAriaLabel)}
            aria-label={getText(TextId.mainCloseAndExitAriaLabel)}
            role="button"
          >
            <img src={cross} style={{ marginTop: "2px" }} alt="Close icon" />
            <span>{getText(TextId.mainCloseAndExitText)}</span>
          </div>
        ) : null}
        <div className="logo-container">
          <img className="logo" src={logo} alt="Zensory logo" />
        </div>
      </div>
      <div
        className="choose-experience-container"
        tabIndex="-1"
        id="choose-naturescape-container"
      >
        <div className="FocusTrack-headingWrapper">
            <h1 className="FocusTrack-heading">
                Step 1. Choose a Music Track
            </h1>
            <p className="FocusTrack-subtitle">To create the most immersive experience we layer music, nature sounds and binaural beats - select your music now.</p>
        </div>
        <Carousel
          experience={props.experience}
          mood={props.mood}
          setExperience={(newExperience) => {
            props.setExperience(newExperience);
            if (props.onChooseMusicTrack) {
              props.onChooseMusicTrack(newExperience);
            }
          }}
        />
        <div className="FocusTrack-trackGuide">
            See what it sounds like:
        </div>
        <div className="audio-player-container">
          <div className="audio-controls">
            <button
              className="play-pause-btn"
              onClick={togglePlayPause}
              aria-label={isPlaying ? "Pause audio" : "Play audio"}
            >
              <img
                src={isPlaying ? pause : play}
                alt={isPlaying ? "Pause" : "Play"}
              />
            </button>
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{ width: `${progressPercentage}%` }}
              >
              </div>
              <div
                className="progress-bar-empty"
                style={{ width: `${100 - progressPercentage}%`}}
              >
              </div>
            </div>
          </div>
        </div>
        <div className="experience-btn-container">
          <button
            id="btn-choose-naturescape-next"
            className="next-button"
            onClick={(e) => handleNextClick(e)}
          >
             Next
          </button>
        </div>
      </div>
    </div>
  );
};

const FocusSoundMusicTrack = withScorm()(FocusSoundMusicTrackPage);

export default FocusSoundMusicTrack;